import { fetchAsset } from "@redux/actions/assets/useAsset";
import { initProduct } from "@redux/actions/products/edit/useInitProduct";
import { duplicateItems } from "@redux/actions/items/useDuplicateItems";
import { fetchGalleryItems } from "@redux/actions/items/useGalleryItems";
import { AppStore } from "@redux/store/reduxStore";
import { Middleware, UnknownAction } from "@reduxjs/toolkit";
import { createKit } from "@redux/actions/kits/useCreateKit";
import { fetchKits } from "@redux/actions/kits/useKits";
import { pollForDuplicateKit } from "@redux/actions/kits/usePollForDuplicateKit";
import { pollForDuplicatePortal } from "@redux/actions/portals/usePollForDuplicatePortal";
import { fetchPortals } from "@redux/actions/portals/usePortals";
import { deleteKit } from "@redux/actions/kits/useDeleteKit";
import { deletePortalItem } from "@redux/actions/portalItems/useDeletePortalItem";
import { removeSharedKit } from "@redux/actions/kits/useRemoveSharedKit";
import { createPortalItems } from "@redux/actions/portalItems/useCreatePortalItems";
import { queryPortalKits } from "@redux/actions/portals/usePortalKits";
import { Item, PortalItem, PortalItemType } from "@thenounproject/lingo-core";
import { deletePortalItems } from "@redux/actions/portalItems/useDeletePortalItems";
import { batchUpdateKitMembers } from "@redux/actions/kitMembers/useBatchUpdateKitMembers";
import { updateEditorStatus } from "@redux/actions/spaceMembers/useUpdateEditorStatus";
import { removePortalMembers } from "@redux/actions/portalMembers/useRemovePortalMembers";
import { updateSubscription } from "@redux/actions/billing/useUpdateSubscription";
import { fetchSpacePlan } from "@redux/actions/billing/useSpacePlan";
import { fetchBillingData } from "@redux/actions/billing/useBillingData";
import { getEntitiesFromAction } from "../reducers/helpers/mergeIfChanged";
import { fetchMarkets } from "@redux/actions/markets/useMarkets";
import { createMarket } from "@redux/actions/markets/useCreateMarket";
import { updateMarket } from "@redux/actions/markets/useUpdateMarket";
import { fetchProducts } from "@redux/actions/products/useProducts";
import { createProduct } from "@redux/actions/products/useCreateProduct";
import { batchUpdateItemStatus } from "@redux/actions/items/useBatchUpdateItemStatus";
import { fetchSectionItems } from "@redux/actions/items/useSectionItems";
import * as actionTypes from "@redux/navPoint/actionTypes";

const mutations: Middleware = (store: AppStore) => next => (action: UnknownAction) => {
  processMutations(store, action);
  const result = next(action);
  postProcessMutations(store, action);
  return result;
};

export default mutations;

function postProcessMutations(store: AppStore, action: UnknownAction) {
  onItemsRestored(store, action);
}

function onItemsRestored(store: AppStore, action: UnknownAction) {
  if (!batchUpdateItemStatus.fulfilled.match(action)) return;
  const { entities, result } = action.payload;
  const restoredItems = result.items
    .filter(i => i.success)
    .map(i => entities.items[i.result] as Item)
    .filter(item => item?.status === "active");

  if (!restoredItems.length) return;

  const state = store.getState(),
    itemsObjects = state.entities.items.objects,
    sections: Record<string, { items: string[] }> = {},
    sectionIds = [...new Set(restoredItems.map(i => i.sectionUuid).filter(Boolean))];

  sectionIds.forEach(sectionId => {
    const [query] = fetchSectionItems.getQueryData(state.entities.items.queries, {
      sectionId,
      version: 0,
    });
    if (!query?.data?.items?.length) return;

    const sorted = [...query.data.items].sort(
      (a, b) => (itemsObjects[a]?.displayOrder ?? 0) - (itemsObjects[b]?.displayOrder ?? 0)
    );
    sections[`${sectionId}-0`] = { items: sorted };
  });

  if (Object.keys(sections).length) {
    store.dispatch({ type: actionTypes.SORT_SECTION_ITEMS, sections });
  }
}

function processMutations(store: AppStore, action: UnknownAction) {
  checkForUploadedGalleries(store, action);
  refetchAsset(store, action);
  refetchKit(store, action);
  refetchPortals(store, action);
  refetchSpacePlan(store, action);
  refetchMarkets(store, action);
  refetchProducts(store, action);
  onKitDeleted(store, action);
  onPortalItemCreated(store, action);
  onPortalItemDeleted(store, action);
  checkForUpdatedKitPermissions(store, action);
  prefetchProductAssets(store, action);
}

function checkForUploadedGalleries(store: AppStore, action: UnknownAction) {
  const items = getEntitiesFromAction<Item>(action, "items");
  if (!items) return;
  const galleries = Object.entries(items).filter(
    item => item[1].version === 0 && item[1].type === "gallery"
  );
  const state = store.getState();
  galleries.forEach(gallery => {
    const galleryItem = gallery[1];
    const existingGallery = state.entities.items.objects[gallery[0]];

    if (!existingGallery) return;
    if (
      existingGallery.data.dateRefreshed !== galleryItem.data.dateRefreshed ||
      existingGallery.data.viewId !== galleryItem.data.viewId
    ) {
      // Clear the query for the gallery
      const invalidateAction = fetchGalleryItems.invalidateAction({
        itemId: galleryItem.uuid,
        version: galleryItem.version,
      });
      store.dispatch(invalidateAction);
    }
  });
}

function refetchAsset(store: AppStore, action: UnknownAction) {
  if (duplicateItems.fulfilled.match(action) && action.meta.arg.createReferences) {
    const {
      result: { items: newItems },
      entities,
    } = action.payload;
    const assetIds = newItems.map(i => entities.items[i].assetUuid);
    assetIds.forEach(assetId => store.dispatch(fetchAsset.invalidateAction({ assetId })));
  }
}

function refetchKit(store: AppStore, action: UnknownAction) {
  if (pollForDuplicateKit.fulfilled.match(action) || createKit.fulfilled.match(action)) {
    const { spaceId } = action.meta.arg;
    store.dispatch(fetchKits.invalidateAction({ spaceId }));
  }
}

function refetchPortals(store: AppStore, action: UnknownAction) {
  if (pollForDuplicatePortal.fulfilled.match(action)) {
    const portal = action.payload.entities.portals[action.payload.result];
    if (portal.status === "active") {
      store.dispatch(fetchPortals.invalidateAction({ spaceId: portal.spaceId }));
    }
  }
}

function refetchSpacePlan(store: AppStore, action: UnknownAction) {
  if (updateSubscription.fulfilled.match(action)) {
    const { spaceId } = action.meta.arg;
    store.dispatch(fetchSpacePlan.invalidateAction({ spaceId }));
    store.dispatch(fetchBillingData.invalidateAction({ spaceId }));
  }
}

function checkForUpdatedKitPermissions(store: AppStore, action: UnknownAction) {
  if (removePortalMembers.fulfilled.match(action)) {
    const { entities, result } = action.payload;
    const state = store.getState();
    const updatedMembers = result.members
      .filter(r => r.success)
      .map(r => entities.portalMembers[r.result]);
    updatedMembers.forEach(portalMember => {
      // Which kits does the user no longer have access to via other portals?
      const kitsInPortal = queryPortalKits.getQueryData(state.entities.kits.queries, {
        portalId: portalMember.portalUuid,
      })[0].data.kits;
      const otherPortalAccess = Object.values(state.entities.portalMembers.objects).filter(
        member =>
          member.userId === portalMember.userId &&
          member.spaceId === portalMember.spaceId &&
          member.status === "active" &&
          member.portalUuid !== portalMember.portalUuid
      );
      const kitsInOtherPortals = otherPortalAccess.reduce((acc, portalAccess) => {
        queryPortalKits
          .getQueryData(state.entities.kits.queries, {
            portalId: portalAccess.portalUuid,
          })[0]
          .data.kits.forEach(kitId => acc.add(kitId));
        return acc;
      }, new Set<string>());

      const removedKits = kitsInPortal.filter(kitId => !kitsInOtherPortals.has(kitId));
      // Nothing to do if all the kits in this portal are still accessible via other portals
      if (removedKits.length === 0) return;

      // Find all kit edit permissions for remaining kits
      const editPermissions = Object.values(state.entities.kitMembers.objects).filter(
        member =>
          member.userId === portalMember.userId &&
          member.spaceId === portalMember.spaceId &&
          member.status === "active" &&
          member.role === "collaborator" &&
          !removedKits.includes(member.kitUuid)
      );
      const hasEditPermission = Boolean(editPermissions.length);
      store.dispatch(
        updateEditorStatus({
          memberId: portalMember.userId,
          spaceId: portalMember.spaceId,
          editorStatus: hasEditPermission,
        })
      );
    });
  }

  if (batchUpdateKitMembers.fulfilled.match(action)) {
    const { entities, result } = action.payload;
    const state = store.getState();
    const updatedMembers = result.members
      .filter(r => r.success)
      .map(r => ({ key: r.result, kitMember: entities.kitMembers[r.result] }));

    updatedMembers.forEach(({ kitMember }) => {
      const member =
        state.entities.spaceMembers.objects[`${kitMember.spaceId}-${kitMember.userId}`];
      if (!member) return;

      const hasEditPermission = Object.values(state.entities.kitMembers.objects).find(member => {
        if (member.userId !== kitMember.userId) return false;
        if (member.spaceId !== kitMember.spaceId) return false;
        const role = member.kitUuid === kitMember.kitUuid ? kitMember.role : member.role;
        return role === "collaborator";
      });

      store.dispatch(
        updateEditorStatus({
          memberId: member.userId,
          spaceId: member.spaceId,
          editorStatus: Boolean(hasEditPermission),
        })
      );
    });
  }
}

function onKitDeleted(store: AppStore, action: UnknownAction) {
  if (deleteKit.fulfilled.match(action) || removeSharedKit.fulfilled.match(action)) {
    // a deleted kit will return the deleted version
    // a removed shared kit will return the kit
    const removedKit =
      action.payload.entities.kitVersions?.[action.payload.result] ??
      action.payload.entities.kits?.[action.payload.result];
    const portalItems = Object.values(store.getState().entities.portalItems.objects).filter(
      pi => pi.kitUuid === removedKit.kitUuid && pi.spaceId === removedKit.spaceId
    );
    portalItems.forEach(portalItem =>
      store.dispatch({
        type: deletePortalItem.fulfilled.type,
        meta: { arg: { portalItemId: portalItem.uuid, portalId: portalItem.portalUuid } },
        payload: {
          result: portalItem.uuid,
          entities: {
            portalItems: { [portalItem.uuid]: { status: "deleted" } },
          },
        },
      })
    );
  }
}

function onPortalItemCreated(store: AppStore, action: UnknownAction) {
  if (createPortalItems.fulfilled.match(action)) {
    const { result, entities } = action.payload;
    const newKits = result.portalItems
      .filter(res => res.success)
      .map(res => entities.portalItems[res.result])
      .filter(item => item.type === PortalItemType.kit);

    if (newKits.length) {
      const portalId = action.meta.arg.portalId;
      store.dispatch(queryPortalKits.invalidateAction({ portalId }));
    }
  }
}

function refetchMarkets(store: AppStore, action: UnknownAction) {
  if (createMarket.fulfilled.match(action) || updateMarket.fulfilled.match(action)) {
    const { spaceId } = action.meta.arg;
    store.dispatch(fetchMarkets.invalidateAction({ spaceId }));
  }
}

function refetchProducts(store: AppStore, action: UnknownAction) {
  if (createProduct.fulfilled.match(action)) {
    const { spaceId } = action.meta.arg;
    store.dispatch(fetchProducts.invalidateAction({ spaceId }));
  }
}

function prefetchProductAssets(store: AppStore, action: UnknownAction) {
  if (!initProduct.match(action)) return;
  const { product, draft } = action.payload;
  const assetStore = store.getState().entities.assets.objects;

  const collectAssetUuids = (fields: Record<string, unknown> | undefined): string[] => {
    if (!fields) return [];
    const out: string[] = [];
    for (const v of Object.values(fields)) {
      if (
        v &&
        typeof v === "object" &&
        "assets" in v &&
        Array.isArray((v as { assets: unknown[] }).assets)
      ) {
        for (const entry of (v as { assets: { assetUuid?: string }[] }).assets) {
          if (entry?.assetUuid) out.push(entry.assetUuid);
        }
      }
    }
    return out;
  };

  const productAssetUuids = draft
    ? collectAssetUuids(draft.fields as Record<string, unknown>)
    : collectAssetUuids(product.fields as Record<string, unknown>);
  const variantAssetUuids = draft
    ? draft.variants.flatMap(v => collectAssetUuids(v.fields as Record<string, unknown>))
    : (product.variants ?? []).flatMap(v => collectAssetUuids(v.fields as Record<string, unknown>));

  [...new Set([...productAssetUuids, ...variantAssetUuids])].forEach(uuid => {
    const existing = assetStore[uuid];
    if (!existing || (!existing.thumbnails && !existing.permalink))
      void store.dispatch(fetchAsset({ args: { assetId: uuid } }));
  });
}

function onPortalItemDeleted(store: AppStore, action: UnknownAction) {
  const deletedItems: PortalItem[] = [];
  if (deletePortalItem.fulfilled.match(action)) {
    const { result, entities } = action.payload;
    const item = entities.portalItems[result];
    deletedItems.push(item);
  } else if (deletePortalItems.fulfilled.match(action)) {
    const { result, entities } = action.payload;
    const items = result.portalItems
      .filter(res => res.success)
      .map(res => entities.portalItems[res.result]);
    deletedItems.push(...items);
  }
  new Set(
    deletedItems.filter(item => item.type === PortalItemType.kit).map(item => item.portalUuid)
  ).forEach(portalId => {
    store.dispatch(queryPortalKits.invalidateAction({ portalId }));
  });
}
